import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { sendWorkshopRegistrationEmail } from '@chancenwerk/workshops/api/sendWorkshopRegistrationMail.js';
import { WorkshopAppointment } from '@chancenwerk/workshops/entities/WorkshopAppointment.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/workshops/entities/WorkshopTrainingAttendance.js';
import { DateTime } from 'luxon';

export const createWorkshopRegistrationExternal = registerMutation({
  type: () => Boolean,
  operationName: 'createWorkshopRegistrationExternal',
  resolve: (email: string, appointmentID: string) => {
    return async () => {
      const userCol = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
      const user = (
        await userCol
          .find({
            $or: [{ email: email }, { emailChancenwerk: email }],
          })
          .toArray()
      )[0];

      // ! If we would error this would be a security issue, as an attacker could check if a user exists.
      // ! It is still possible through timing attacks.
      // TODO: Implement a timing safe operation
      if (!user) {
        logger.warn(`User with email ${email} not found`);
        return true;
      }

      const appointment = await WorkshopAppointment.getByID<WorkshopAppointment>(appointmentID)({
        ...WorkshopAppointment.allFields,
        workshop: {
          title: 1,
          appointmentInfo: 1,
          requiresTraining: 1,
        },
      });

      if (!appointment) {
        logger.warn(`Appointment with ID ${appointmentID} not found`);
        throw new Error('Appointment not found');
      }

      const attendanceSameDay = await loadMongoData(WorkshopTrainingAttendance, {
        userID: user._id,
        workshopID: appointment.workshopID,
        $and: [
          {
            date: {
              $gte: (appointment.start ?? DateTime.now()).startOf('day').toJSDate(),
            },
          },
          {
            date: {
              $lte: (appointment.start ?? DateTime.now()).endOf('day').toJSDate(),
            },
          },
        ],
      });

      if (attendanceSameDay.length > 0) {
        logger.warn(`User with ID ${user._id} already registered for workshop with ID ${appointment.workshopID}`);
        return true;
      }

      const attendanceBefore = await loadMongoData(WorkshopTrainingAttendance, {
        userID: user._id,
        workshopID: appointment.workshopID,
        state: {
          $in: [WorkshopAttendanceStatus.REGISTERED, WorkshopAttendanceStatus.ATTENDED],
        },
      });

      if (attendanceBefore.length > 0) {
        throw new Error(
          'Das hat leider nicht geklappt. Entweder du hast an dieser Veranstaltung bereits teilgenommen oder bist bereits zu einem Termin angemeldet. Jede Veranstaltung der CHANCENakademie darf grundsätzlich nur einmal besucht werden. Ausnahmen müssen im Vorhinein mit deiner ZP und dem Team der CHANCENakademie abgesprochen werden. Melde dich dafür und auch bei Fragen bei events@chancenwerk.org.',
        );
      }

      const accessToken = Array.from({ length: 64 }, () => Math.floor(Math.random() * 36).toString(36)).join('');

      if (appointment.workshop?.requiresTraining) {
        const newAttendance = new WorkshopTrainingAttendance({
          userID: user._id,
          workshopID: appointment.workshopID,
          date: appointment.start,
          state: WorkshopAttendanceStatus.REGISTERED,
          accessToken,
        });
        await newAttendance.create();
      } else {
        const newAttendance = new WorkshopAttendance({
          userID: user._id,
          workshopID: appointment.workshopID,
          date: appointment.start,
          state: WorkshopAttendanceStatus.REGISTERED,
          accessToken,
        });
        await newAttendance.create();
      }

      await sendWorkshopRegistrationEmail(appointmentID, user._id)();

      return true;
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'email' },
    { type: () => String, name: 'appointmentID' },
  ],
});
